<template>
  <!-- 遮罩层 -->
  <div class="modal-overlay" @click="handleOverlayClick">
    <!-- 弹窗容器 -->
    <div class="modal-container" @click.stop>
      <!-- Loading状态 -->
      <div v-if="isPopupLoading" class="popup-loading-container">
        <van-loading type="spinner" color="#17B663" size="24px" />
      </div>

      <!-- 弹窗内容 - 只在数据加载完成后显示 -->
      <div v-show="!isPopupLoading">
        <!-- 弹窗头部 -->
        <div class="modal-header">
          <div></div>
          <h3 class="modal-title">{{ t('mysteriousMine.title') }}</h3>
           <img  :src="getAssetsFile('activity/dazhuanpan/close3.png')" alt="" class="w-[16px] h-[16px]"  @click="handleClose">
        </div>

        <div  class="anniuser" >
          <div class="aniusbuttons" :class="buttonClass" @click="handleButtonClick">
            {{ buttonText }}
            <!-- 镜片效果，只在可领取状态显示 -->
            <div v-if="buttonState === 'active'" class="button-shine"></div>
          </div>
          <div class="color-[#ECA33A]  text-[13px] mt-[12px]">{{ t('mysteriousMine.mineOpens') }} {{ numberFormat(wankdata?.showSingleMaxReward)  }}</div>
          <div class="timedoges">
              <div class="timedoges-item" v-for="(item,index) in wankdata.mysteriousMineTimeList" :key="index">
                      <div class="timedoges-item" :class="{ 'active-time': item.flag, 'inactive-time': !item.flag }">
                          {{formatTime(item.startTime)}}-{{formatTime(item.endTime)}}
                      </div>
              </div>
          </div>
          <div class="color-[#493738] text-[10px] w-[80%]" style="text-align: left;">
          <div  class="mt-[10px]">{{ t('mysteriousMine.collectUpTo') }}</div>
           <div  class="mt-[5px]">{{ t('mysteriousMine.freeForDepositUsers') }}</div>
           <div  class="mt-[5px]">{{ t('mysteriousMine.crystalsWillBeExchanged') }}</div>
           <div class="mt-[5px] ">{{ t('mysteriousMine.balanceAtRate') }}</div>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- 在模板最后添加 RewardDialog 组件 -->
  <RewardDialog
    v-model:show="showRewardDialog"
    :amount="rewardAmount"
    @confirm="handleRewardConfirm"
  />
</template>

<script setup lang="ts">
import { defineEmits, onMounted, onBeforeUnmount, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { numberFormat } from '@/utils/formatter'
import { getAssetsFile } from '@/utils/pub-use'
import * as GameApi from '@/api/game'
import RewardDialog from '@/components/RewardDialog.vue' // 导入 RewardDialog 组件

// 多语言支持
const { t } = useI18n()

// 定义事件
const emit = defineEmits<{
  close: []
  confirm: []
}>()

// 按钮状态：'active' | 'completed' | 'countdown'
const buttonState = ref('countdown')

// 倒计时时间（秒）
const countdownTime = ref(0)
const wankdata = ref<any>({})

// 弹窗加载状态 - 等待背景图片加载
const isPopupLoading = ref(true)

// 奖励金额
const rewardAmount = ref<any>({})
// 是否显示奖励弹窗
const showRewardDialog = ref(false)

// 格式化时间，只显示小时和分钟
const formatTime = (timeString: string) => {
  if (!timeString) return ''
  // 如果时间格式是 "HH:MM:SS"，只取前5位 "HH:MM"
  return timeString.substring(0, 5)
}

// 获取神秘矿洞倒计时数据
const getMineCountdown = async () => {
  try {
    const res = await GameApi.CountdownToMine()
    console.log(res.data, 'wakdog倒计时数据')
    wankdata.value = res.data

    // 根据 hasClaimed 字段判断按钮状态
    if (res.data && res.data.hasClaimed !== undefined) {
      switch (res.data.hasClaimed) {
        case 0:
          buttonState.value = 'completed' // 不可领取
          break
        case 1:
          buttonState.value = 'active' // 可领取
          break
        case 2:
          buttonState.value = 'countdown' // 倒计时中
          break
        default:
          buttonState.value = 'countdown'
      }
    }

    // 如果有倒计时数据，设置倒计时时间
    if (res.data && res.data.countDown) {
      countdownTime.value = res.data.countDown
      // 只有在倒计时状态时才启动倒计时
      if (buttonState.value === 'countdown') {
        startCountdown()
      }
    }

  } catch (error) {
    console.error('获取神秘矿洞倒计时失败:', error)
    // 请求失败时停止定时器，避免无限循环
    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
    // 设置默认状态
    buttonState.value = 'countdown'
    countdownTime.value = 0
  }
}

// 计算按钮样式类
const buttonClass = computed(() => {
  switch (buttonState.value) {
    case 'active':
      return 'aniusbuttons-active'
    case 'completed':
    case 'countdown':
      return 'aniusbuttons-inactive'
    default:
      return 'aniusbuttons-active'
  }
})

// 计算按钮文字
const buttonText = computed(() => {
  switch (buttonState.value) {
    case 'active':
      return t('mysteriousMine.mining')
    case 'completed':
      return t('mysteriousMine.alreadyMined')
    case 'countdown':
      const hours = Math.floor(countdownTime.value / 3600)
      const minutes = Math.floor((countdownTime.value % 3600) / 60)
      const seconds = countdownTime.value % 60
      const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      return `${t('mysteriousMine.closed')}  ${t('mysteriousMine.opensSoon')} ${timeStr}`
    default:
      return t('mysteriousMine.mining')
  }
})

// 按钮点击处理
const handleButtonClick = async () => {
  if (buttonState.value === 'active') {
    // 可领取状态，调用领奖接口
    try {
      console.log('开始领取奖励')
      const res = await GameApi.CountdownreceiveTheAward()
      if (res.code == 0 || res.code == 200) {
        // 显示奖励弹窗，传递领取的金额
        rewardAmount.value = res.data
        showRewardDialog.value = true

        // 注意：这里不立即刷新数据，等弹窗确认后再刷新
        // await getMineCountdown() // 注释掉这行，等弹窗确认后再刷新
      } else {
        console.error('领取奖励失败')
      }
    } catch (error) {
      console.error('领取奖励失败:', error)
      console.error('领取奖励失败')
    }
  }
}

// 处理奖励确认
const handleRewardConfirm = async () => {
  // 弹窗关闭后的处理逻辑
  console.log('奖励已确认')

  // 等待一下确保API处理完成
  await new Promise(resolve => setTimeout(resolve, 500))

  // 刷新数据，这会更新按钮状态
  await getMineCountdown()
}

// 倒计时定时器
let countdownTimer: NodeJS.Timeout | null = null

// 开始倒计时
const startCountdown = () => {
  // 清除之前的定时器
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }

  countdownTimer = setInterval(() => {
    if (countdownTime.value > 0) {
      countdownTime.value--
    } else {
      // 倒计时结束，重新获取状态
      // 添加延迟避免立即重试
      setTimeout(() => {
        getMineCountdown()
      }, 5000) // 5秒后重试
    }
  }, 1000)
}

// 禁止滚动
const disableScroll = () => {
  document.body.style.overflow = 'hidden'
  document.body.style.position = 'fixed'
  document.body.style.width = '100%'
}

// 恢复滚动
const enableScroll = () => {
  document.body.style.overflow = ''
  document.body.style.position = ''
  document.body.style.width = ''
}

// 关闭弹窗
const handleClose = () => {
  enableScroll()
  emit('close')
}

// 确认操作
const handleConfirm = () => {
  enableScroll()
  emit('confirm')
}

// 点击遮罩层关闭弹窗
const handleOverlayClick = () => {
  handleClose()
}

// 预加载背景图片
const preloadBackgroundImage = () => {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      console.log('背景图片加载完成')
      isPopupLoading.value = false
      resolve(true)
    }
    img.onerror = () => {
      console.log('背景图片加载失败，继续显示弹窗')
      isPopupLoading.value = false
      resolve(false)
    }
    // 使用 getAssetsFile 获取正确的图片路径
    img.src = getAssetsFile('activity/dogactivity.png')
  })
}

// 组件挂载时禁止滚动并获取倒计时数据
onMounted(async () => {
  disableScroll()

  // 先预加载背景图片
  await preloadBackgroundImage()

  // 获取倒计时数据
  await getMineCountdown()
})

// 组件卸载前恢复滚动
onBeforeUnmount(() => {
  enableScroll()
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>

<style lang="scss" scoped>
/* 遮罩层 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* 弹窗容器 */
.modal-container {
  border-radius: 12px;
  max-width: 90%;
  max-height: 90%;
  width: 400px;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
  background-image: getPath('activity/dogactivity.png');
  background-size: 100% 100%;
  padding-bottom: 20px;
  min-height: 300px; /* 确保有足够高度显示loading */
}

/* Loading容器样式 */
.popup-loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
}

/* 弹窗动画 */
@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 弹窗头部 */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  position: relative;
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: bold;
  color: #FFFFFF;
  position: absolute;
  text-shadow: 2px 3px 0px #7C5F1A;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}

.anniuser{
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

/* 活跃状态按钮样式（绿色） */
.aniusbuttons-active {
    position: relative; /* 添加相对定位 */
    width: 80%;
    height: 35px;
    background: #7BDE42;
    border-radius: 5px;
    font-size: 13px;
    color: #fff;
    line-height: 35px;
    text-align: center;
    margin-top: 52%;
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden; /* 隐藏超出部分 */
}

/* 非活跃状态按钮样式（灰色） */
.aniusbuttons-inactive {
    position: relative; /* 添加相对定位 */
    width: 80%;
    height: 35px;
  background: linear-gradient(180deg, #227F36 0%, #256C2E 100%);
    border-radius: 5px;
    font-size: 13px;
    color: #50B388;
    line-height: 35px;
    font-weight: bold;
    text-align: center;
    margin-top: 52%;
    cursor: not-allowed;
    overflow: hidden; /* 隐藏超出部分 */
}

/* 移动端适配 */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 20px;
  }
}
.timedoges{
    border-radius:10px ;
    border: 1px solid #A36333;
    background: #1A0E0F;
    height: 51px;
    width: 80%;
    margin-top: 11px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    padding:5px 8px 5px 13px ; /* 修改右边距从13px改为8px */
    overflow: hidden;
    overflow-y: auto;

    /* 添加滚动条样式 */
    scrollbar-width: thin; /* Firefox */
    scrollbar-color: #ECA33A transparent; /* Firefox */
}

/* Webkit浏览器滚动条样式 */
.timedoges::-webkit-scrollbar {
    width: 6px;
}

.timedoges::-webkit-scrollbar-track {
    background:transparent !important;
    border-radius: 3px;
}

.timedoges::-webkit-scrollbar-thumb {
    background: #ECA33A;
    border-radius: 3px;
}

.timedoges::-webkit-scrollbar-thumb:hover {
    background: #F19445;
}

.timedoges-item{
    color: #ECA33A;
    font-size:12px;
}

/* 活跃时间段样式（黄色） */
.active-time {
    color: #ECA33A !important;
    font-weight: bold;
}

/* 非活跃时间段样式（灰色） */
.inactive-time {
    color: #6A6A6A !important;
    opacity: 0.6;
}
/* 按钮镜片滑动动画 */
.button-shine {
    position: absolute;
    top: -50%;
    left: -100%;
    width: 100%;
    height: 200%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
    transform: rotate(45deg);
    animation: shine 3s infinite;
    z-index: 1;
}

@keyframes shine {
    0% {
        left: -100%;
        opacity: 0;
    }
    5% {
        opacity: 1;
    }
    25% {
        left: 100%;
        opacity: 1;
    }
    30% {
        left: 100%;
        opacity: 0;
    }
    100% {
        left: 100%;
        opacity: 0;
    }
}

/* 响应式调整 */
@media (max-width: 500px) {
    .saque-popup-content {
        padding: 15px;
    }

    .balance-value {
        font-size: 20px;
    }

    .balance-currency {
        font-size: 14px;
    }
}
</style>
